import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import JobApplicationForm from './JobApplicationForm';

const Careers = () => {
  const { t } = useTranslation();
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);

  const jobs = t('careers.jobs', { returnObjects: true });
  const benefits = t('careers.benefits.items', { returnObjects: true });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setShowApplicationForm(true);
  };

  const closeApplicationForm = () => {
    setShowApplicationForm(false);
    setSelectedJob(null);
  };

  return (
    <div className="min-h-screen bg-black text-white py-20">
      <div className="container mx-auto px-6">
        
        {/* Header Section */}
        <motion.div 
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.h1 
            className="text-5xl md:text-7xl font-bold mb-6"
            variants={itemVariants}
          >
            <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              {t('careers.title')} 
            </span>
            <span className="bg-gradient-to-r from-cyan-400 to-cyan-600 bg-clip-text text-transparent ml-4">
              {t('careers.titleHighlight')}
            </span>
          </motion.h1>
          
          <motion.p 
            className="text-xl text-gray-300 max-w-3xl mx-auto mb-8"
            variants={itemVariants}
          >
            {t('careers.subtitle')}
          </motion.p>
          
          <motion.div
            className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-cyan-600 mx-auto"
            variants={itemVariants}
          />
        </motion.div>

        {/* Benefits Section */}
        <motion.div 
          className="mb-20"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.h2 
            className="text-3xl font-bold text-center mb-12"
            variants={itemVariants}
          >
            {t('careers.benefits.title')}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="bg-gray-900 p-6 rounded-lg border border-gray-800 hover:border-cyan-500 transition-colors duration-300"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.05, 
                  boxShadow: "0 10px 30px rgba(0, 255, 255, 0.2)" 
                }}
              >
                <h3 className="text-xl font-semibold text-cyan-400 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-300">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Open Positions Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div className="text-center mb-12" variants={itemVariants}>
            <h2 className="text-3xl font-bold mb-4">
              {t('careers.positions.title')}
            </h2>
            <p className="text-xl text-gray-300">
              {t('careers.positions.subtitle')}
            </p>
          </motion.div>

          <div className="space-y-8">
            {jobs.map((job, index) => (
              <motion.div
                key={job.id}
                className="bg-gray-900 border border-gray-800 rounded-lg p-8 hover:border-cyan-500 transition-all duration-300"
                variants={itemVariants}
                whileHover={{ 
                  y: -5,
                  boxShadow: "0 10px 30px rgba(0, 255, 255, 0.1)" 
                }}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                  <div className="mb-4 lg:mb-0">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {job.title}
                    </h3>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-300">
                      <span className="bg-gray-800 px-3 py-1 rounded-full">
                        {job.department}
                      </span>
                      <span className="bg-gray-800 px-3 py-1 rounded-full">
                        📍 {job.location}
                      </span>
                      <span className="bg-gray-800 px-3 py-1 rounded-full">
                        ⏰ {job.type}
                      </span>
                      <span className="bg-gray-800 px-3 py-1 rounded-full">
                        💼 {job.experience}
                      </span>
                    </div>
                  </div>
                  
                  <motion.button
                    onClick={() => handleApplyClick(job)}
                    className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-black font-semibold px-8 py-3 rounded-lg hover:from-cyan-400 hover:to-cyan-500 transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t('careers.joinTeam')}
                  </motion.button>
                </div>

                <p className="text-gray-300 mb-6">
                  {job.description}
                </p>

                {/* About Company Section */}
                {job.aboutCompany && (
                  <div className="mb-6 p-4 bg-gray-800 rounded-lg border-l-4 border-cyan-500">
                    <h4 className="text-lg font-semibold text-cyan-400 mb-3">
                      {t('careers.labels.aboutCompany')}
                    </h4>
                    <p className="text-gray-300">
                      {job.aboutCompany}
                    </p>
                  </div>
                )}

                {/* Responsibilities Section */}
                {job.responsibilities && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-cyan-400 mb-3">
                      {t('careers.labels.responsibilities')}
                    </h4>
                    <ul className="space-y-2">
                      {job.responsibilities.map((resp, respIndex) => (
                        <li key={respIndex} className="text-gray-300 flex items-start">
                          <span className="text-cyan-400 mr-2">•</span>
                          {resp}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-cyan-400 mb-3">
                      {t('careers.labels.requirements')}
                    </h4>
                    <ul className="space-y-2">
                      {job.requirements.map((req, reqIndex) => (
                        <li key={reqIndex} className="text-gray-300 flex items-start">
                          <span className="text-cyan-400 mr-2">•</span>
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Nice to Have Section */}
                  {job.niceToHave && (
                    <div>
                      <h4 className="text-lg font-semibold text-green-400 mb-3">
                        {t('careers.labels.niceToHave')}
                      </h4>
                      <ul className="space-y-2">
                        {job.niceToHave.map((nice, niceIndex) => (
                          <li key={niceIndex} className="text-gray-300 flex items-start">
                            <span className="text-green-400 mr-2">+</span>
                            {nice}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div>
                    <h4 className="text-lg font-semibold text-cyan-400 mb-3">
                      {t('careers.labels.benefits')}
                    </h4>
                    <ul className="space-y-2">
                      {job.benefits.map((benefit, benefitIndex) => (
                        <li key={benefitIndex} className="text-gray-300 flex items-start">
                          <span className="text-cyan-400 mr-2">•</span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* General Application CTA */}
        <motion.div 
          className="text-center mt-16 p-8 bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg border border-gray-700"
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <h3 className="text-2xl font-bold mb-4">
            Aradığınız pozisyonu bulamadınız mı?
          </h3>
          <p className="text-gray-300 mb-6">
            Bize CV'nizi gönderin, uygun pozisyon açıldığında sizinle iletişime geçelim.
          </p>
          <motion.button
            onClick={() => handleApplyClick({ title: 'Genel Başvuru', id: 'general' })}
            className="bg-gradient-to-r from-green-500 to-green-600 text-black font-semibold px-8 py-3 rounded-lg hover:from-green-400 hover:to-green-500 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Genel Başvuru Yap
          </motion.button>
        </motion.div>
      </div>

      {/* Application Form Modal */}
      {showApplicationForm && (
        <JobApplicationForm 
          job={selectedJob}
          onClose={closeApplicationForm}
        />
      )}
    </div>
  );
};

export default Careers;